import { DataService } from 'api/DataService'

import { SaasPlanKey } from 'utils/saas-checkout-event'

import {
  SaasBilling,
  SaasCheckoutResult,
  SaasSubscription
} from './SaasCheckout.types'

type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; message: string; code?: string }

// DataService devolve o response do axios no sucesso e um objeto { error, message }
// na falha. Normaliza os dois para quem chama não precisar conhecer o formato.
const unwrap = <T>(response: any, fallback: string): ApiResult<T> => {
  if (!response || response.error || response.data?.error) {
    return {
      ok: false,
      message: response?.message || response?.data?.message || fallback,
      code: response?.data?.code || response?.code
    }
  }
  return { ok: true, data: response.data.record as T }
}

export const fetchSaasSubscription = async () =>
  unwrap<SaasSubscription>(
    await DataService({ type: 'GET', url: '/v1/saas_subscription' }),
    'Não foi possível consultar sua assinatura.'
  )

export const createSaasCheckout = async (
  planKey: SaasPlanKey,
  customer?: Record<string, unknown>,
  setup = false
) =>
  unwrap<SaasCheckoutResult>(
    await DataService({
      type: 'POST',
      url: '/v1/saas_subscription/checkout',
      data: { plan: planKey, customer, setup }
    }),
    'Não foi possível gerar a contratação.'
  )

export const fetchSaasBilling = async (uuid: string) =>
  unwrap<SaasBilling>(
    await DataService({
      type: 'GET',
      url: `/v1/saas_subscription/billings/${uuid}`
    }),
    'Não foi possível consultar a cobrança.'
  )

export const generatePix = async (billingUuid: string) => {
  const response = await DataService({
    type: 'POST',
    url: '/v1/transactions/pix',
    data: { billing_uuid: billingUuid }
  })
  if (!response || response.error || response.data?.error) {
    // A API recusa a mesma requisição repetida em poucos segundos (409 com
    // retry_after); não é falha do PIX, é só pedir de novo depois.
    const duplicate = response?.status === 409
    return {
      ok: false as const,
      duplicate,
      retryAfter: Number(response?.data?.retry_after) || 3,
      message: response?.message || 'Não foi possível gerar o PIX.'
    }
  }
  return {
    ok: true as const,
    emv: response.data.emv as string,
    expiry: response.data.expiry as string
  }
}

export type CardPayload = {
  billing_uuid: string
  holder_name: string
  document: string
  card_number: string
  expiration_month: string
  expiration_year: string
  cvv: string
  payment_type: 'credit_card' | 'credit_card_recurring'
}

export const payWithCard = async (payload: CardPayload) => {
  const response = await DataService({
    type: 'POST',
    url: '/v1/transactions/charge_card',
    data: payload
  })
  if (!response || response.error || response.data?.error) {
    return {
      ok: false as const,
      message: response?.message || 'Pagamento recusado.'
    }
  }
  return { ok: true as const, message: response.data.message as string }
}

/** Cartão parcelado (SC-11592): o cartão não fica salvo, a renovação chega como fatura. */
export const payWithCardInstallments = async (
  payload: Omit<CardPayload, 'payment_type'> & { installments: number }
) => {
  const response = await DataService({
    type: 'POST',
    url: '/v1/transactions/charge_card_installments',
    data: payload
  })
  if (!response || response.error || response.data?.error) {
    return {
      ok: false as const,
      message: response?.message || 'Pagamento recusado.'
    }
  }
  return { ok: true as const, message: response.data.message as string }
}

export const payWithSavedCard = async (billingUuid: string) =>
  unwrap<SaasBilling>(
    await DataService({
      type: 'POST',
      url: '/v1/saas_subscription/pay_with_saved_card',
      data: { billing_uuid: billingUuid }
    }),
    'Não foi possível cobrar no cartão salvo.'
  )

export const fetchSaasPortalLink = async () =>
  unwrap<{ url: string }>(
    await DataService({
      type: 'GET',
      url: '/v1/saas_subscription/portal_link'
    }),
    'Não foi possível abrir o portal de faturas.'
  )
